import type { ApiRequest, ApiRequestWith } from "@/api/types";
import type {
  DraftNote,
  PatientConsent,
  PatientStatus,
  ProviderProfile,
  UpdateProviderBody,
  UUID,
} from "@/core/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";

type FetchProviderDraftNotesBody = {
  provider_id?: UUID;
};

type UpdatePatientStatusBody = {
  providerName?: string;
  reason?: string;
  status: PatientStatus;
  username: UUID;
};

export type UpdatePatientStatusResponse = {
  previous_status: PatientStatus;
};

type ProviderRequestsTyped = {
  delete_progress_note: ApiRequestWith<
    void,
    { body: { id: UUID }; urlParams: { patientId: UUID } }
  >;
  fetch_provider_draft_notes: ApiRequest<DraftNote[], { body?: FetchProviderDraftNotesBody }>;
  fetch_provider_profile: ApiRequest<
    { user: ProviderProfile },
    { searchParams?: { provider_id?: UUID } }
  >;
  get_patient_consents: ApiRequestWith<PatientConsent[], { urlParams: { patientId: UUID } }>;
  update_patient_status: ApiRequestWith<
    UpdatePatientStatusResponse,
    { body: UpdatePatientStatusBody; urlParams: { patientId: UUID } }
  >;
  update_profile: ApiRequestWith<
    void,
    { body: UpdateProviderBody; searchParams?: { provider_id?: UUID } }
  >;
};

export const providerRequestsTyped: ProviderRequestsTyped = {
  delete_progress_note: function ({ body, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/delete_progress_notes`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<void>();
  },

  fetch_provider_draft_notes: function ({ body, context } = {}) {
    return httpAuth2
      .post("/v1/provider/get_provider_draft_notes", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(body !== undefined && { json: body }),
      })
      .json<DraftNote[]>();
  },

  fetch_provider_profile: function ({ context, searchParams } = {}) {
    return httpAuth2
      .post("/v1/provider/profile_get", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams, // ky omits keys with undefined values, so passing unconditionally is safe
      })
      .json<{ user: ProviderProfile }>();
  },

  get_patient_consents: function ({ context, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/get_latest_consents`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<PatientConsent[]>();
  },

  update_patient_status: function ({ body, context, urlParams }) {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/update_patient_details`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<UpdatePatientStatusResponse>();
  },

  update_profile: function ({ body, context, searchParams }) {
    return httpAuth2
      .post("/v2/provider/profile_update", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
        searchParams, // ky omits keys with undefined values, so passing unconditionally is safe
      })
      .json<void>();
  },
};
